import { OnboardingHeader } from '@/features/onboarding/OnboardingHeader';
import { BankAccountTrainingCombined } from '@/features/onboarding/combined/BankAccountTrainingCombined';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { storeMappedDataForModule } from '@/lib/onboardingDataSync';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';

export const OnboardingBankAccountTraining = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleComplete = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data: progress } = await supabase
        .from('onboarding_progress')
        .select('sections_completed')
        .eq('company_id', companyId)
        .single();

      const sectionsCompleted = (progress?.sections_completed as any) || {};
      const phase2Sections = sectionsCompleted.phase2 || {};
      phase2Sections.bank_accounts_phase2 = {
        status: 'completed',
        completedAt: new Date().toISOString(),
      };
      
      // Also mark bank_accounts as completed
      sectionsCompleted.bank_accounts = {
        status: 'completed',
        completedAt: new Date().toISOString(),
      };

      await supabase
        .from('onboarding_progress')
        .update({
          sections_completed: {
            ...sectionsCompleted,
            phase2: phase2Sections,
          },
          last_activity: new Date().toISOString(),
        })
        .eq('company_id', companyId);

      // Update bank_account_training_progress to mark all accounts as completed
      const { error: progressError } = await supabase
        .from('bank_account_training_progress')
        .update({ status: 'completed' })
        .eq('company_id', companyId)
        .neq('status', 'completed');
      
      if (progressError) {
        console.warn('Error updating bank account training progress:', progressError);
      }

      toast({ title: 'Saving your data…' });
      const syncResult = await storeMappedDataForModule(companyId, 'bank_accounts');
      if (!syncResult.success) {
        console.error('storeMappedDataForModule bank_accounts failed:', syncResult.error);
        toast({
          title: 'Data sync had issues',
          description: 'Your progress was saved but some data may not appear in reports yet. It will sync automatically on next login.',
          variant: 'destructive',
        });
      }
      navigate('/onboarding');
    } catch (error) {
      console.error('Error completing bank account training:', error);
      toast({
        title: 'Could not save progress',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSkip = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data: progress } = await supabase
        .from('onboarding_progress')
        .select('sections_completed')
        .eq('company_id', companyId)
        .single();

      const sectionsCompleted = (progress?.sections_completed as any) || {};
      const phase2Sections = sectionsCompleted.phase2 || {};
      phase2Sections.bank_accounts_phase2 = {
        status: 'skipped',
        completedAt: new Date().toISOString(),
      };

      await supabase
        .from('onboarding_progress')
        .update({
          sections_completed: {
            ...sectionsCompleted,
            phase2: phase2Sections,
          },
          last_activity: new Date().toISOString(),
        })
        .eq('company_id', companyId);

      navigate('/onboarding');
    } catch (error) {
      console.error('Error skipping bank account training:', error);
    }
  };

  const handleSaveAndReturn = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data: progress } = await supabase
        .from('onboarding_progress')
        .select('sections_completed')
        .eq('company_id', companyId)
        .single();

      const sectionsCompleted = (progress?.sections_completed as any) || {};
      const phase2Sections = sectionsCompleted.phase2 || {};
      
      if (!phase2Sections.bank_accounts_phase2 || phase2Sections.bank_accounts_phase2.status !== 'completed') {
        phase2Sections.bank_accounts_phase2 = {
          ...phase2Sections.bank_accounts_phase2,
          status: 'in_progress',
          lastSaved: new Date().toISOString(),
        };

        await supabase
          .from('onboarding_progress')
          .update({
            sections_completed: {
              ...sectionsCompleted,
              phase2: phase2Sections,
            },
            last_activity: new Date().toISOString(),
          })
          .eq('company_id', companyId);
      }

      toast({
        title: 'Progress Saved',
        description: 'Your progress has been saved.',
      });

      navigate('/onboarding');
    } catch (error) {
      console.error('Error saving progress:', error);
      navigate('/onboarding');
    }
  };

  return (
    <OnboardingProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
        <OnboardingHeader />
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <BankAccountTrainingCombined 
              onComplete={handleComplete}
              onSkip={handleSkip}
              onSaveAndReturn={handleSaveAndReturn}
            />
          </motion.div>
        </div>
      </div>
    </OnboardingProvider>
  );
};

